/**
 * AdminSystemStatus — Iter 133 (+133c).
 *
 * Single-pane-of-glass operational health board:
 *   1. SMS delivery       (Infobip)
 *   2. Email delivery     (Resend)
 *   3. Backup runs        (Backblaze B2)
 *
 * Each section is a self-contained card so we can add / reorder / hide
 * without touching the others. Every panel exposes the same contract:
 *   • summary strip with Delivered / Pending / Failed counts
 *   • rolling recent-activity table
 *   • per-row "Resend" or "Details" action where meaningful
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  RefreshCcw, Send, CheckCircle2, XCircle, Clock, Ban, Loader2,
  Mail, HardDrive,
} from 'lucide-react';

import { api, formatApiError } from '../../lib/api';

// ---------------------------------------------------------------------------
// Status buckets — shared by SMS, Email and Backup rows.
// ---------------------------------------------------------------------------
const SMS_SUCCESS = new Set(['DELIVERED_TO_HANDSET', 'DELIVERED_TO_OPERATOR']);
const SMS_PENDING = new Set([
  'PENDING', 'PENDING_ACCEPTED', 'PENDING_ENROUTE', 'PENDING_WAITING_DELIVERY',
  'MESSAGE_ACCEPTED',
]);
const SMS_FAILED = new Set([
  'REJECTED_NETWORK', 'REJECTED_PREFIX_MISSING', 'REJECTED_DESTINATION',
  'REJECTED_SOURCE', 'REJECTED_NOT_ENOUGH_CREDITS',
  'EXPIRED', 'UNDELIVERABLE_NOT_DELIVERED', 'UNDELIVERABLE_REJECTED_OPERATOR',
  'MISSING_TO',
]);

function smsBucket(s) {
  if (!s) return 'unknown';
  if (SMS_SUCCESS.has(s)) return 'delivered';
  if (SMS_FAILED.has(s)) return 'failed';
  if (SMS_PENDING.has(s)) return 'pending';
  return 'unknown';
}

function emailBucket(s) {
  if (!s) return 'unknown';
  if (s === 'SENT') return 'delivered';
  if (s === 'FAILED' || s === 'SKIPPED_NO_KEY') return 'failed';
  return 'unknown';
}

function backupBucket(s) {
  if (!s) return 'unknown';
  if (s === 'completed') return 'delivered';
  if (s === 'failed') return 'failed';
  if (s === 'queued' || s === 'running') return 'pending';
  return 'unknown';
}


const BUCKET_META = {
  delivered: { icon: CheckCircle2, color: 'text-[#1B5E20]', bg: 'bg-[#1B5E20]/10', label: 'Delivered' },
  pending:   { icon: Clock,        color: 'text-[#DA8B00]', bg: 'bg-[#DA8B00]/10', label: 'Pending'   },
  failed:    { icon: XCircle,      color: 'text-[#B83A3A]', bg: 'bg-[#B83A3A]/10', label: 'Failed'    },
  unknown:   { icon: Ban,          color: 'text-fg-muted',  bg: 'bg-fg-muted/10',  label: 'Unknown'   },
};

function StatusPill({ bucket, status }) {
  const m = BUCKET_META[bucket] || BUCKET_META.unknown;
  const Icon = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${m.color} ${m.bg}`}
      title={status || '—'}
      data-testid={`status-pill-${bucket}`}
    >
      <Icon className="w-3 h-3"/> {m.label}
    </span>
  );
}


// ---------------------------------------------------------------------------
// Reusable summary-strip: 4 filter chips (total + 3 buckets).
// ---------------------------------------------------------------------------
function SummaryStrip({ rows, bucketOf, filter, setFilter, testidPrefix }) {
  const counts = useMemo(() => {
    const out = { delivered: 0, pending: 0, failed: 0, unknown: 0 };
    for (const r of rows) out[bucketOf(r)]++;
    return out;
  }, [rows, bucketOf]);
  const chip = (id, label, count, tone) => (
    <button type="button" onClick={() => setFilter(id)}
      className={`text-left rounded-lg border p-3 transition ${filter === id ? `border-${tone.border} bg-${tone.bg}` : 'border-soft bg-white hover:border-brand'}`}
      data-testid={`${testidPrefix}-filter-${id}`}>
      <div className={`text-[11px] uppercase tracking-wider ${tone.text}`}>{label}</div>
      <div key={`${testidPrefix}-${id}-${count}`} className={`text-2xl font-heading font-bold ${tone.text}`}>{count}</div>
    </button>
  );
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid={`${testidPrefix}-summary`}>
      <button type="button" onClick={() => setFilter('all')}
        className={`text-left rounded-lg border p-3 transition ${filter === 'all' ? 'border-brand bg-brand/5' : 'border-soft bg-white hover:border-brand'}`}
        data-testid={`${testidPrefix}-filter-all`}>
        <div className="text-[11px] uppercase tracking-wider text-fg-muted">Total</div>
        <div key={`${testidPrefix}-total-${rows.length}`} className="text-2xl font-heading font-bold">{rows.length}</div>
      </button>
      {chip('delivered', 'Delivered', counts.delivered, { border: '[#1B5E20]', bg: '[#1B5E20]/5', text: 'text-[#1B5E20]' })}
      {chip('pending',   'Pending',   counts.pending,   { border: '[#DA8B00]', bg: '[#DA8B00]/5', text: 'text-[#DA8B00]' })}
      {chip('failed',    'Failed',    counts.failed,    { border: '[#B83A3A]', bg: '[#B83A3A]/5', text: 'text-[#B83A3A]' })}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Generic hook: fetch a delivery-log endpoint + expose refresh handle.
// ---------------------------------------------------------------------------
function useDeliveryLog(path, tick) {
  const [state, setState] = useState({ rows: [], providerEnabled: true, loading: true, err: '' });
  useEffect(() => {
    let cancelled = false;
    setState(s => ({ ...s, loading: true }));
    api.get(path)
      .then(r => {
        if (cancelled) return;
        setState({
          rows: r.data.rows || [],
          providerEnabled: !!r.data.provider_enabled,
          loading: false,
          err: '',
        });
      })
      .catch(e => {
        if (cancelled) return;
        setState({ rows: [], providerEnabled: false, loading: false,
                   err: formatApiError(e.response?.data?.detail) || e.message });
      });
    return () => { cancelled = true; };
  }, [path, tick]);
  return state;
}


// ---------------------------------------------------------------------------
// SMS section
// ---------------------------------------------------------------------------
function SmsSection({ tick, onRefresh }) {
  const { rows, providerEnabled, loading, err } = useDeliveryLog('/sms/log', tick);
  const [filter, setFilter] = useState('all');
  const [resending, setResending] = useState({});
  const filtered = filter === 'all' ? rows : rows.filter(r => smsBucket(r.delivery_status) === filter);

  const resend = async (msgId) => {
    setResending(x => ({ ...x, [msgId]: true }));
    try {
      const { data } = await api.post(`/sms/log/${msgId}/resend`);
      toast.success(`SMS resent — new id …${data.new_message_id?.slice(-6) || ''}`);
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setResending(x => ({ ...x, [msgId]: false }));
    }
  };

  return (
    <section className="mb-10" data-testid="section-sms">
      <div className="flex items-center gap-2 mb-3">
        <Send className="w-5 h-5 text-brand"/>
        <h2 className="font-heading font-bold text-xl">SMS (Infobip)</h2>
      </div>
      {!providerEnabled && (
        <div className="mb-3 rounded-lg border border-[#DA8B00]/40 bg-[#DA8B00]/5 px-3 py-2 text-xs" data-testid="sms-provider-warning">
          <strong>Not configured</strong> — set <code>INFOBIP_BASE_URL</code> + <code>INFOBIP_API_KEY</code> in server env.
        </div>
      )}
      <SummaryStrip rows={rows} bucketOf={r => smsBucket(r.delivery_status)}
                    filter={filter} setFilter={setFilter} testidPrefix="sms"/>
      <LogTable
        loading={loading} err={err} rows={filtered} testid="sms-log-table"
        emptyMsg="No SMSes sent yet. Enable SMS reminders + consent for a patient with a phone number."
        columns={[
          { header: 'When',    render: r => fmtWhen(r.sent_at) },
          { header: 'Purpose', render: r => r.purpose || '—' },
          { header: 'Phone',   render: r => <span className="font-mono">{r.phone || '—'}</span> },
          {
            header: 'Status',
            render: r => (
              <div>
                <StatusPill bucket={smsBucket(r.delivery_status)} status={r.delivery_status}/>
                {r.delivery_description && (
                  <div className="text-[10px] text-fg-muted mt-0.5 max-w-[220px] truncate" title={r.delivery_description}>
                    {r.delivery_description}
                  </div>
                )}
              </div>
            ),
          },
          { header: 'Body', className: 'max-w-[280px] truncate', render: r => <span title={r.body}>{r.body}</span> },
          {
            header: 'Actions', align: 'right',
            render: r => {
              if (r.resent_to_message_id) {
                return (
                  <span className="text-[10px] text-fg-muted italic" data-testid={`sms-already-resent-${r.message_id}`}>
                    Resent →&nbsp;…{r.resent_to_message_id.slice(-6)}
                  </span>
                );
              }
              const b = smsBucket(r.delivery_status);
              if (b !== 'failed' && b !== 'unknown') return <span className="text-[10px] text-fg-muted">—</span>;
              return (
                <button type="button"
                  className="text-xs rounded-md border border-brand px-3 py-1 text-brand hover:bg-brand hover:text-white transition disabled:opacity-50"
                  onClick={() => resend(r.message_id)} disabled={!!resending[r.message_id]}
                  data-testid={`sms-resend-${r.message_id}`}>
                  {resending[r.message_id] ? 'Sending…' : 'Resend'}
                </button>
              );
            },
          },
        ]}
      />
    </section>
  );
}


// ---------------------------------------------------------------------------
// Email section
// ---------------------------------------------------------------------------
function EmailSection({ tick, onRefresh }) {
  const { rows, providerEnabled, loading, err } = useDeliveryLog('/email/log', tick);
  const [filter, setFilter] = useState('all');
  const [resending, setResending] = useState({});
  const filtered = filter === 'all' ? rows : rows.filter(r => emailBucket(r.delivery_status) === filter);

  const resend = async (emailId) => {
    setResending(x => ({ ...x, [emailId]: true }));
    try {
      const { data } = await api.post(`/email/log/${emailId}/resend`);
      toast.success(`Email resent — new id …${data.new_email_id?.slice(-8) || ''}`);
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setResending(x => ({ ...x, [emailId]: false }));
    }
  };

  return (
    <section className="mb-10" data-testid="section-email">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-5 h-5 text-brand"/>
        <h2 className="font-heading font-bold text-xl">Email (Resend)</h2>
      </div>
      {!providerEnabled && (
        <div className="mb-3 rounded-lg border border-[#DA8B00]/40 bg-[#DA8B00]/5 px-3 py-2 text-xs" data-testid="email-provider-warning">
          <strong>Not configured</strong> — set <code>RESEND_API_KEY</code> in server env.
        </div>
      )}
      <SummaryStrip rows={rows} bucketOf={r => emailBucket(r.delivery_status)}
                    filter={filter} setFilter={setFilter} testidPrefix="email"/>
      <LogTable
        loading={loading} err={err} rows={filtered} testid="email-log-table"
        emptyMsg="No outbound emails yet. Reminders and confirmations will appear here once patients start booking."
        columns={[
          { header: 'When',    render: r => fmtWhen(r.sent_at) },
          { header: 'Purpose', render: r => r.purpose || 'transactional' },
          { header: 'To',      render: r => <span className="font-mono text-xs">{r.to || '—'}</span> },
          {
            header: 'Status',
            render: r => (
              <div>
                <StatusPill bucket={emailBucket(r.delivery_status)} status={r.delivery_status}/>
                {r.error && (
                  <div className="text-[10px] text-fg-muted mt-0.5 max-w-[220px] truncate" title={r.error}>
                    {r.error}
                  </div>
                )}
              </div>
            ),
          },
          { header: 'Subject', className: 'max-w-[280px] truncate', render: r => <span title={r.subject}>{r.subject || '—'}</span> },
          {
            header: 'Actions', align: 'right',
            render: r => {
              if (r.resent_to_email_id) {
                return (
                  <span className="text-[10px] text-fg-muted italic" data-testid={`email-already-resent-${r.email_id}`}>
                    Resent →&nbsp;…{r.resent_to_email_id.slice(-8)}
                  </span>
                );
              }
              const b = emailBucket(r.delivery_status);
              if (b !== 'failed' && b !== 'unknown') return <span className="text-[10px] text-fg-muted">—</span>;
              if (!r.email_id) return <span className="text-[10px] text-fg-muted italic">no-id</span>;
              return (
                <button type="button"
                  className="text-xs rounded-md border border-brand px-3 py-1 text-brand hover:bg-brand hover:text-white transition disabled:opacity-50"
                  onClick={() => resend(r.email_id)} disabled={!!resending[r.email_id]}
                  data-testid={`email-resend-${r.email_id}`}>
                  {resending[r.email_id] ? 'Sending…' : 'Resend'}
                </button>
              );
            },
          },
        ]}
      />
    </section>
  );
}


// ---------------------------------------------------------------------------
// Backup section
// ---------------------------------------------------------------------------
function BackupSection({ tick }) {
  const { rows, providerEnabled, loading, err } = useDeliveryLog('/backups/history', tick);
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? rows : rows.filter(r => backupBucket(r.status) === filter);

  return (
    <section className="mb-10" data-testid="section-backups">
      <div className="flex items-center gap-2 mb-3">
        <HardDrive className="w-5 h-5 text-brand"/>
        <h2 className="font-heading font-bold text-xl">Backups (Backblaze B2)</h2>
      </div>
      {!providerEnabled && (
        <div className="mb-3 rounded-lg border border-[#DA8B00]/40 bg-[#DA8B00]/5 px-3 py-2 text-xs" data-testid="backup-provider-warning">
          <strong>Not configured</strong> — set <code>B2_KEY_ID</code>, <code>B2_APPLICATION_KEY</code>, <code>B2_BUCKET_NAME</code> in server env.
        </div>
      )}
      <SummaryStrip rows={rows} bucketOf={r => backupBucket(r.status)}
                    filter={filter} setFilter={setFilter} testidPrefix="backup"/>
      <LogTable
        loading={loading} err={err} rows={filtered} testid="backup-history-table"
        emptyMsg="No backup runs yet. The scheduler fires at 03:00 UTC daily — you can also trigger one from Data migration."
        columns={[
          { header: 'When',    render: r => fmtWhen(r.queued_at) },
          { header: 'Backup id', render: r => <span className="font-mono text-xs">{r.backup_id || '—'}</span> },
          { header: 'Trigger',   render: r => r.auto ? 'scheduler' : 'manual' },
          {
            header: 'Status',
            render: r => (
              <div>
                <StatusPill bucket={backupBucket(r.status)} status={r.status}/>
                {r.error && (
                  <div className="text-[10px] text-fg-muted mt-0.5 max-w-[240px] truncate" title={r.error}>
                    {r.error}
                  </div>
                )}
              </div>
            ),
          },
          {
            header: 'Size',
            render: r => r.bytes
              ? <span className="font-mono text-xs">{prettyBytes(r.bytes)}</span>
              : <span className="text-[11px] text-fg-muted">—</span>,
          },
          {
            header: 'Object', align: 'right',
            render: r => r.object_key
              ? <span className="font-mono text-[10px] text-fg-muted truncate max-w-[220px] inline-block" title={r.object_key}>{r.object_key}</span>
              : <span className="text-[10px] text-fg-muted">—</span>,
          },
        ]}
      />
    </section>
  );
}


// ---------------------------------------------------------------------------
// Generic log-table used by all three sections.
// ---------------------------------------------------------------------------
function LogTable({ loading, err, rows, columns, testid, emptyMsg }) {
  return (
    <div className="rounded-lg border border-soft bg-white overflow-hidden" data-testid={testid}>
      {loading ? (
        <div className="p-6 text-center text-fg-muted text-sm">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2"/> Loading…
        </div>
      ) : err ? (
        <div className="p-4 text-sm text-[#B83A3A]" data-testid={`${testid}-error`}>{err}</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-center text-fg-muted text-sm" data-testid={`${testid}-empty`}>
          {emptyMsg}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F2EBDB]/50 text-[11px] uppercase tracking-wider text-fg-muted">
              <tr>
                {columns.map((c, i) => (
                  <th key={i} className={`px-4 py-2 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-soft hover:bg-[#F2EBDB]/20">
                  {columns.map((c, j) => (
                    <td key={j} className={`px-4 py-2 ${c.className || ''} ${c.align === 'right' ? 'text-right' : ''}`}>
                      {c.render(r)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Formatting helpers.
// ---------------------------------------------------------------------------
function fmtWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
  return (
    <span className="whitespace-nowrap text-xs text-fg-muted font-mono">
      {d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
    </span>
  );
}

function prettyBytes(n) {
  if (!n || n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}


// ---------------------------------------------------------------------------
// Page shell — 3 stacked sections + shared refresh button.
// ---------------------------------------------------------------------------
export default function AdminSystemStatus() {
  const [tick, setTick] = useState(0);
  const refresh = () => setTick(t => t + 1);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="admin-system-status">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl mb-1">System status</h1>
          <p className="text-sm text-fg-muted">
            Live view of every outbound integration — SMS, email, and backup runs.
            Bookmark this page and glance at it each morning before your first patient.
          </p>
        </div>
        <button type="button"
          className="btn-secondary text-sm inline-flex items-center gap-2"
          onClick={refresh}
          data-testid="refresh-all">
          <RefreshCcw className="w-4 h-4"/> Refresh
        </button>
      </header>

      <SmsSection tick={tick} onRefresh={refresh}/>
      <EmailSection tick={tick} onRefresh={refresh}/>
      <BackupSection tick={tick}/>

      <div className="text-[11px] text-fg-muted mt-6 leading-relaxed">
        Green = terminal success. Amber = still in flight (Infobip retries up to 20&times;, Resend does not).
        Red = failed — click Resend on that row and check the details in the provider portal.
        Backup runs are scheduled daily at 03:00 UTC; manual runs can be triggered from Data migration.
      </div>
    </div>
  );
}
